import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { EntityType } from '@models/common/entity-type.enum';
import { ExternalEntity } from '@models/integrations/external-entity';
import { ExternalSourceType } from '@models/integrations/external-source-type.enum';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CommonModalFormComponent } from "@shared/components/commons/common-modal-form/common-modal-form.component";
import { AgOpenActionComponent } from '@shared/components/grid/ag-cells/common/ag-open-action/ag-open-action.component';
import {
  GridClientSideComponent
} from "@shared/components/grid/components/grid-client-side/grid-client-side.component";
import { ButtonComponent } from '@shared/controls/button/button.component';
import { EntityIdentityApiService } from '@shared/services/entity-identity-api.service';
import { StringHelpService } from '@shared/services/string-help.service';
import { convertEnumToFilterOption } from '@shared/utils/enum-to-filter-option.helper';
import { getCommonActionCellColDef } from '@shared/utils/grid/get-common-column-chooser-params';
import { ColDef, GridApi } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, finalize, tap } from 'rxjs';

@Component({
  selector: 'app-link-to-external-entity-modal',
  standalone: true,
  imports: [
    ButtonComponent,
    CommonModalFormComponent,
    GridClientSideComponent
  ],
  templateUrl: './link-to-external-entity-modal.component.html',
  styleUrl: './link-to-external-entity-modal.component.scss'
})
export class LinkToExternalEntityModalComponent implements OnInit {
  @Input() entityType!: EntityType;
  @Input() entityId!: string;
  @Input() entityNumber!: string;
  @Input() sourceType!: ExternalSourceType;

  @Output() confirm = new EventEmitter<ExternalEntity>();

  private entityIdentityApiService = inject(EntityIdentityApiService);
  private toastr = inject(ToastrService);
  private activeModal = inject(NgbActiveModal);
  protected modalService = inject(NgbModal);

  externalEntities: ExternalEntity[] = [];
  checkedExternalEntities: ExternalEntity[] = [];
  loadingGrid = false;
  loadingLinkIdentity = false;
  isEntityNumberEqual = true;
  moreThenOneEntitySelected = false;

  ngOnInit(): void {
    this.getExternalEntityList();
    this.setColDef();
  }

  openProfile(externalEntity: ExternalEntity | undefined) {
    if (!externalEntity?.linkTo || externalEntity.linkTo == "") {
      return;
    }

    window.open(externalEntity.linkTo, '_blank');
  }

  getExternalEntityList() {
    this.loadingGrid = true;
    let searchByTypes = [EntityType.Company, EntityType.Trust];

    if (this.entityType == EntityType.Individual) {
      searchByTypes = [EntityType.Individual];
    }

    this.entityIdentityApiService.list(this.sourceType, searchByTypes)
      .subscribe({
        next: result => this.externalEntities = result,
        error: (error) => {
          if (this.entityIdentityApiService.checkSyncError(error)) {
            this.activeModal.close();
            return;
          }

          this.toastr.error('Failed to get external entity list');
        },
        complete: () => {
          this.loadingGrid = false;
        }
      });
  }


  linkIdentity() {
    if (this.checkedExternalEntities.length > 1) {
      this.toastr.warning('Please select only one external entity.');
      return;
    }

    this.loadingLinkIdentity = true;

    this.entityIdentityApiService.linkEntity(
      this.entityId,
      this.checkedExternalEntities[0].externalId,
      this.entityType
    ).pipe(
      tap(() => {
        this.toastr.success('Entity successfully linked.', 'Success');
        this.confirm.emit(this.checkedExternalEntities[0]);
        this.activeModal.close();
      }),
      catchError((error: HttpErrorResponse) => {
        if (this.entityIdentityApiService.checkSyncError(error)) {
          this.activeModal.close();
          return EMPTY;
        }

        this.toastr.error('Failed to link entity');
        return EMPTY;
      }),
      finalize(() => {
        this.loadingLinkIdentity = false;
      })
    ).subscribe();
  }

  close(): void {
    this.activeModal.dismiss();
  }

  get isLinkButtonEnabled() {
    return this.checkedExternalEntities.length != 1;
  }

  get differentEnitityNumbersMessage() {
    const entityNumberName = this.entityType == EntityType.Company ? 'ACN' : 'ABN';
    const entityName = this.entityType == EntityType.Company ? 'companies' : 'trusts';
    const message = `**Warning:** A discrepancy has been found in the ${ entityNumberName }s of the ${ entityName } you want to link. Please check that the ${ entityNumberName }s match, as linking mismatched ${ entityName } may cause errors.`;
    return message;
  }

  // =====  Grid  =====

  colDefs: ColDef[] = [];
  gridApi!: GridApi;

  onGridReady(gridApi: GridApi): void {
    this.gridApi = gridApi;
  }

  selectEntities(externalEntities: ExternalEntity[]): void {
    this.checkedExternalEntities = externalEntities;
    this.moreThenOneEntitySelected = this.checkedExternalEntities.length > 1;
    this.isEntityNumberEqual = this.checkedExternalEntities.length == 1
      ? StringHelpService.compareStringWithoutSpaces(this.checkedExternalEntities[0].entityNumber, this.entityNumber)
      : true;
  }

  setColDef() {
    this.colDefs = [
      {
        headerName: 'Entity Name',
        field: 'name',
        sort: 'desc',
        flex: 1,
      },
      {
        headerName: 'Entity Number',
        field: 'entityNumber',
        flex: 0.5,
      },
      {
        headerName: 'Entity Type',
        field: 'type',
        flex: 0.5,
        valueFormatter: (params: { value: EntityType }) => {
          return EntityType[params.value];
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: convertEnumToFilterOption(EntityType),
          valueFormatter: (params: { value: number }) => {
            return params.value !== undefined && params.value !== null ? EntityType[params.value] : '';
          }
        }
      },
      {
        headerName: 'Entity adress',
        field: 'address.normalizedFullAddress',
        flex: 1,
      },
      {
        ...getCommonActionCellColDef(this.colDefs),
        field: '',
        width: 50,
        sortable: false,
        cellClass: 'actions-button-cell',
        cellRenderer: AgOpenActionComponent,
        cellRendererParams: {
          open: this.openProfile.bind(this),
        },
      },
    ];
  }
}
